/* eslint-disable @nx/enforce-module-boundaries*/
import 'regenerator-runtime/runtime';
import { SagaIterator } from 'redux-saga';
import { all, call, cancel, select, take, takeEvery } from 'redux-saga/effects';
import {
  Adresse,
  AngebotsId,
  DoRequestResponse,
  GlobalAction,
  GlobalActions,
  GlobalEventDispatcher,
  GlobalPut,
  isAzam,
  OwnedAngebotsId,
  ProduktDeleted,
  ProvideStoreCreationDataAction,
  PS20Store,
  SavedState,
  SDWAbsprungData,
  storeIsAvailable,
  WrappedSavedStates,
} from '@ps20-frontend/shop-integration-redux';
import { App, isMultistoreApp } from '@ps20-frontend/frontend-models';
import { vwgStoreFactory } from '@ps20-frontend/apps/vwg-store';
import { vwgV2StoreFactory } from '@ps20-frontend/apps/vwg-store-v2';
import { tkvStoreFactory } from '@ps20-frontend/apps/tkv-store';
import { tkvStoreFactory as tkvV2StoreFactory } from '@ps20-frontend/apps/tkv-store-v2';
import { vhvStoreFactory } from '@ps20-frontend/apps/vhv-store';
import { vhvStoreFactory as vhvV2StoreFactory } from '@ps20-frontend/apps/vhv-store-v2';
import { unfStoreFactory } from '@ps20-frontend/apps/unf-store';
import { AVLadeVorgangResponse } from '@ps20/bff-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { gegeStoreFactory } from '@ps20-frontend/apps/gege-store';
import { IAppState } from '../localStore';
import { isSdwSession } from '../selectors/transientState.selector';
import { filterOutInactiveProductVersions, loadSavedAv } from './loadAvSaga';

export function* watchStoreManagementSaga(): SagaIterator {
  yield takeEvery(GlobalActions.LOAD_AV_REQUEST_SUCCESSFUL, safeSaga(handleLoadedStates, App.SHOP));
  yield takeEvery(
    GlobalActions.ANGEBOT_BERECHNEN_IN_DASHBOARD_CLICKED,
    safeSaga(handleNewAngebot, App.SHOP),
  );
  yield takeEvery(GlobalActions.ADD_VORVERTRAG_TO_ANGEBOTE, safeSaga(handleNewAngebot, App.SHOP));
  yield takeEvery(GlobalActions.ADD_PRODUKT_TO_ANGEBOTE, safeSaga(handleNewAngebot, App.SHOP));
  yield takeEvery(GlobalActions.PRODUKT_DELETED, safeSaga(handleAngebotDeleted, App.SHOP));
}

function* handleLoadedStates(action: DoRequestResponse<AVLadeVorgangResponse>): SagaIterator {
  if (action.payload.capFrontendUrl) {
    return;
  }
  const state: IAppState = yield select();
  const featureToggles = state.transientState.featureToggles;
  const loadedStates = filterOutInactiveProductVersions(loadSavedAv(action.payload.stores), featureToggles);
  const toolbarAppState: SavedState = loadedStates.find(
    loadedState => loadedState.appId === App.SHOP_TOOLBAR,
  )?.state;
  const statesMissingStores: WrappedSavedStates = getStatesMissingStore(loadedStates);
  yield all(
    statesMissingStores.map(state =>
      call(
        createOrReuseStore,
        state.appId,
        state.angebotsId,
        toolbarAppState,
        state.state?.risikoort,
      ),
    ),
  );
  yield call(GlobalPut.getInstance().put, new GlobalActions().allMissingStoresCreated());
  yield call(
    GlobalPut.getInstance().put,
    new GlobalActions().setPriceGuarantee(action.payload.priceGuarantee),
  );
}

function getStatesMissingStore(payload: WrappedSavedStates): WrappedSavedStates {
  return payload.filter(({ appId }) => isMultistoreApp(appId));
}

export function* handleNewAngebot(action: GlobalAction<OwnedAngebotsId>): SagaIterator {
  const { angebotsId, appId, ownerAppId } = action.payload;
  if (storeIsAvailable({ angebotsId, appId })) {
    return yield cancel();
  }

  if (!angebotsId || !isMultistoreApp(appId)) {
    return yield cancel();
  }
  yield call(createOrReuseStore, appId, angebotsId, null, null, action);
  if (ownerAppId) {
    yield call(
      GlobalPut.getInstance().put,
      new GlobalActions().ownedStoreInitialized({ appId, angebotsId }),
    );
  }
}

function* createOrReuseStore(
  appId: App,
  angebotsId: string,
  toolbarAppState: SavedState,
  savedRisikoort: Adresse,
  action?: GlobalAction<AngebotsId>,
): SagaIterator {
  const globalEventDispatcher = GlobalEventDispatcher.getInstance();
  const reactivatedRemovedStore = globalEventDispatcher.reactivateRemovedStore(appId);

  const aStore = reactivatedRemovedStore?.store || createStreckenStore(appId);

  aStore.dispatch(new GlobalActions().angebotsIdReceived(angebotsId));

  if (action?.type === GlobalActions.ADD_VORVERTRAG_TO_ANGEBOTE) {
    aStore.dispatch(action);
  }

  if (action?.type === GlobalActions.ADD_PRODUKT_TO_ANGEBOTE) {
    aStore.dispatch(action);
  }

  if (
    action?.type === GlobalActions.ANGEBOT_BERECHNEN_IN_DASHBOARD_CLICKED &&
    action.payload.angebotsdatum
  ) {
    aStore.dispatch(new GlobalActions().addProduktToAngebote(appId, action.payload.angebotsdatum));
  }

  const state: IAppState = yield select();
  if (action) {
    aStore.dispatch(new GlobalActions().featuretoggleReceived(state.transientState.featureToggles));
    aStore.dispatch(new GlobalActions().settingsReceived(state.transientState.settings));
  }
  GlobalEventDispatcher.getInstance().dispatch(
    new GlobalActions().requestInitialStoreManagementData(),
  );

  const { payload: storeCreationData }: ProvideStoreCreationDataAction = yield take(
    GlobalActions.INITIAL_STORE_MANAGEMENT_DATA_PROVIDED,
  );
  let vnAdresseSavedAsRisikoort = false;
  if (isAzam(state.transientState.role) && toolbarAppState && savedRisikoort) {
    if (savedVnAdresseAsRisikoort(savedRisikoort, toolbarAppState)) {
      vnAdresseSavedAsRisikoort = true;
    }
  }

  const kdvDaten = state.transientState.datenhaltung.kdv;
  const sdwAbsprungData: SDWAbsprungData =
    state.transientState.datenhaltung.sdw && isSdwSession(state)
      ? {
          partyId: state.transientState.datenhaltung.sdw.partyId,
          role: state.transientState.datenhaltung.sdw.role,
        }
      : null;
  aStore.dispatch(
    new GlobalActions().commonInsuranceDataReceived({
      amisSessionId: kdvDaten.sessionId,
      sdw: sdwAbsprungData,
      versicherungsnehmer: storeCreationData.versicherungsnehmer,
      vertragsoptionen: storeCreationData.vertragsoptionen,
      vnAdresseSavedAsRisikoort,
      vnKundenverwaltungID: kdvDaten.perId,
      role: state.transientState.role,
      vermittlernummer: state.transientState.loadVorvertraege.vermittlernummer,
      vnInvalidAddress: state.transientState.vnAddressInvalid.invalidAddressConfirmed,
      kooperationen: storeCreationData?.kooperationen,
    }),
  );
  globalEventDispatcher.addStore(appId, aStore, angebotsId);
}

function createStreckenStore(appId: App): PS20Store<IAppState> {
  const nextIndexForAppId = GlobalEventDispatcher.getInstance().nextStoreIndexFor(appId);
  const storeFactoriesFunction = ({
    [App.VWG]: vwgStoreFactory,
    [App.VWG_V2]: vwgV2StoreFactory,
    [App.TKV]: tkvStoreFactory,
    [App.VHV]: vhvStoreFactory,
    [App.VHV_V2]: vhvV2StoreFactory,
    [App.UNF]: unfStoreFactory,
    [App.GEGE]: gegeStoreFactory,
    [App.TKV_V2]: tkvV2StoreFactory,
  } as unknown as Partial<Record<App, (storeIndex: number) => PS20Store<IAppState>>>)[appId];

  return storeFactoriesFunction(nextIndexForAppId);
}

function handleAngebotDeleted(action: GlobalAction<ProduktDeleted>): void {
  const ged = GlobalEventDispatcher.getInstance();

  action.payload.angebotsIds
    .filter(angebotsId => isMultistoreApp(angebotsId.appId))
    .forEach(angebotsId => ged.removeStore(angebotsId));
}

function savedVnAdresseAsRisikoort(savedRisikoort: Adresse, toolbarAppState: SavedState) {
  const vnAdresse = toolbarAppState.versicherungsnehmer?.adresse;
  return (
    vnAdresse.ort === savedRisikoort.ort &&
    vnAdresse.plz === savedRisikoort.plz &&
    vnAdresse.strasse === savedRisikoort.strasse &&
    vnAdresse.hausnummer === savedRisikoort.hausnummer &&
    (savedRisikoort.hausnrzus && savedRisikoort.hausnrzus !== ''
      ? savedRisikoort.hausnrzus === vnAdresse.hausnrzus
      : true)
  );
}
